import Button from "@/components/forms/Button/Button";
import { BxError, ErrorCode, errorCodeIsGeneric, ErrorSource } from "@boxem/core-api-schema";
import React, { createContext, useContext, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import { logRocketError } from "@/utils/logUtils";
import Flex from "@/components/Flex/Flex";


const ErrorModalContext = createContext(undefined);
export const useErrorModalContext = () => useContext<{

  // call this in your catch block of a API request that may fail generically
  // if you want to defer handling of the generic error to the modal hosted in this
  // context. returns true if we handled it. false means the error is non-generic.
  handleErrorResponseGenerically: (errors: BxError[]) => boolean;

  // call this if you want to know if we 'would' handle a generic error here,
  // but don't actually do the handling. We'll return an array of errors 
  // that your calling code can actually handle.
  //wouldHandleErrorResponseGenerically: (errors: BxError[]) => BxError[];

  // explicitly open the modal:
  setErrors: (errors: BxError[]) => void;

}>(ErrorModalContext);


export const ErrorModalContextProvider = (props: { children: React.ReactNode }) => {
  const [errors, setErrors] = useState<BxError[]>([]);

  const wouldHandleErrorResponseGenerically = (_errors: any): BxError[] => {
    const errorsAreNotArrayOrIsEmpty: boolean = !Array.isArray(_errors) || _errors.length === 0;
  
    if (errorsAreNotArrayOrIsEmpty) {
      // if we don't get back an array at all, or it's empty, we treat this as a generic error.
      // we'll have to fabricate an error here.
      return [{ source: ErrorSource.BOXEM, message: "An unknown error occured." }];

    } else {
      const isAllGenericErrors = _errors.every(error => errorCodeIsGeneric(error.type));
      if (isAllGenericErrors) {
        // if every error is generic, we'll handle it generically here.
        // if we instead have a mix of generic and non-generic errors, right now we'll handle the non-
        // generic error at the call-site, and ignore the generic one.
        return _errors;
      }
    }
    return [];
  }

  return (
    <ErrorModalContext.Provider
      value={{
        handleErrorResponseGenerically: (_errors: any) => {

          if (errors?.length > 0) {
            console.info("Deciding to not open an error modal for errors because we already had a modal open. Errors already displayed: ", errors, "Errors we wanted to display: ", _errors);
            return false;
          }

          _errors = wouldHandleErrorResponseGenerically(_errors);
          if (_errors.length > 0) {
            logRocketError(`Received generic errors: ${JSON.stringify(_errors)}`);
            setErrors(_errors);
            return true;
          }
          return false;
        },
        setErrors
      }}
    >
      <>
        {props.children}

        <Modal
          closeOnOutsideClick={false}
          open={errors?.length > 0}
          onOpenChange={open => {
            if (!open) {
              setErrors([]);
            }
          }}
        >
          <ModalHeader>
            <h3>Error Encountered</h3>
          </ModalHeader>

          <ModalBody>
            <Flex direction="column" style={{margin:"20px"}}>
              <div style={{ marginTop: "1rem" }}>
                <p>Sorry, something isn't working as expected...</p>

                {errors.map((error, index) => {
                  switch (error.type) {
                    case ErrorCode.POLLING_WAS_ABORTED_BECAUSE_OF_EXCEEDING_MAX_ITERATIONS:
                      error.message = "While waiting for a result from Amazon, we timed-out. Things were taking long than expected."
                      break;
                  }

                  return (
                    <Flex key={index} direction="column" gap={4}>
                      {error.code === "InternalServerError" && (
                        <span>Error from Amazon:</span>
                      )}
                      <p
                        style={{
                          background: "var(--static-background-weak)",
                          padding: "1rem",
                          borderRadius: "10px",
                          fontFamily: "Inter",
                          marginTop: "0.5rem",
                        }}
                      >
                        {error.code && (
                          <span>
                            Code: {error.code}
                            <br />
                            <br />
                          </span>
                        )}
                        {error.message}
                        {error.details}
                      </p>
                    </Flex>
                  )
                })}

                <p style={{paddingTop:"10px"}}>You can try:</p>
                <ul style={{ lineHeight: "2" }}>
                  <li>refreshing the page</li>
                  <li>trying again later</li>
                  <li>contacting us</li>
                </ul>
              </div>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Flex justify="end" gap={2}>
              <Button
                size="large"
                type="secondary"
                onClick={() => {
                  setErrors([]);
                }}
              >
                Close
              </Button>
              <Button
                size="large"
                onClick={() => {
                  setErrors([]);
                }}
              >
                OK
              </Button>
            </Flex>
          </ModalFooter>
        </Modal>
      </>
    </ErrorModalContext.Provider>
  );
}
